/* src/utils/calcROI.js */
/**
 * Calculates ROI as a numeric percentage based on price, rent, and expenses.
 * Returns the numeric ROI (e.g., 5.2) or null if not calculable.
 * @param {Object} params - Input parameters. All numeric/string inputs are parsed to numbers.
 * @param {number|string} params.price - Property price
 * @param {number|string} [params.closingCosts] - One-time closing costs (optional)
 * @param {number|string} [params.inspectionCost] - One-time inspection cost (optional)
 * @param {number|string} [params.additionalCosts] - Other one-time initial costs (optional)
 *
 * @param {number|string} [params.managementFeePercentage] - Estimated monthly property management fee as a percentage of rent (optional)
 * @param {number|string} [params.maintenance] - Estimated annual maintenance costs (optional)
 * @param {number|string} [params.utilities] - Monthly utilities paid by owner (optional)
 * @param {number|string} [params.otherExpenses] - Other recurring monthly expenses (optional)
 *
 * @param {number|string} [params.hoa] - Monthly HOA fee (optional)
 * @param {number|string} [params.propertyTax] - Annual property tax (optional)
 * @param {number|string} [params.insurance] - Annual insurance cost (optional)
 *
 * @param {number|string} [params.vacancyRate] - Estimated vacancy rate as a percentage (e.g., 5 for 5%) (optional)
 * @param {number|string} params.rent - Monthly rent estimate
 *
 * @returns {number | null} Numeric ROI percentage or null.
 */
export const calcROI = ({ price, rent, hoa, propertyTax, insurance, inspectionCost, closingCosts, additionalCosts, managementFeePercentage, maintenance, utilities, otherExpenses, vacancyRate }) => {
  const parsedPrice = Number(price);
  const parsedRent = Number(rent);

  // Essential inputs: price and rent must be positive numbers
  if (!parsedPrice || !parsedRent || parsedPrice <= 0 || parsedRent <= 0) {
    return null;
  }

  // Calculate Total Initial Investment
  const parsedInspectionCost = Number(inspectionCost) || 0;
  const parsedClosingCosts = Number(closingCosts) || 0;
  const parsedAdditionalCosts = Number(additionalCosts) || 0;
  const totalInitialInvestment = parsedPrice + parsedInspectionCost + parsedClosingCosts + parsedAdditionalCosts;

  // Calculate Adjusted Annual Income (accounting for vacancy)
  const annualGrossRent = parsedRent * 12;
  const parsedVacancyRate = Number(vacancyRate) || 0; // Default to 0% if not provided
  const vacancyLoss = annualGrossRent * (parsedVacancyRate / 100);
  const adjustedAnnualIncome = annualGrossRent - vacancyLoss;

  // Calculate annual expenses (HOA and Utilities are monthly, PropertyTax and Insurance are annual)
  const annualHoa = Number(hoa) ? Number(hoa) * 12 : 0;
  const annualPropertyTax = Number(propertyTax) || 0;
  const annualInsurance = Number(insurance) || 0; // Insurance is now annual

  // Calculate annual management fee from percentage and rent
  const parsedManagementFeePercentage = Number(managementFeePercentage) || 0;
  const annualManagementFee = parsedRent * (parsedManagementFeePercentage / 100) * 12;

  const annualMaintenance = Number(maintenance) || 0;
  const annualUtilities = Number(utilities) ? Number(utilities) * 12 : 0;
  const annualOtherExpenses = Number(otherExpenses) ? Number(otherExpenses) * 12 : 0;

  const totalAnnualOperatingExpenses = annualHoa + annualPropertyTax + annualInsurance + annualManagementFee + annualMaintenance + annualUtilities + annualOtherExpenses;

  // ROI calculation
  // Ensure totalInitialInvestment is positive to avoid division by zero or meaningless ROI
  if (totalInitialInvestment <= 0) {
    return null;
  }

  const netAnnualProfit = adjustedAnnualIncome - totalAnnualOperatingExpenses;
  const roi = (netAnnualProfit / totalInitialInvestment) * 100;

  return isFinite(roi) ? parseFloat(roi.toFixed(1)) : null;
};
